import { Overlay } from '@blueprintjs/core'
import React from 'react'

import {
  DateTimePickerPopover,
  IDateTimePickerPopoverProps,
} from 'browser/components/atomic-elements/atoms/date-picker/date-time-picker-popover'
import { TimezoneListStep } from './timezone-list-step'

import './_date-time-bottom-sheet.scss'

type SheetStep = 'picker' | 'timezone'

interface IDateTimeBottomSheetProps {
  isOpen: boolean
  onClose: () => void
  title: string
  /**
   * Props forwarded to the DateTimePickerPopover rendered inside the sheet.
   * `hideFooter` + `inline` are injected by the sheet so the picker shows
   * only its picker controls (no nested card chrome, no Apply button); the
   * sheet provides its own Apply affordance in the header.
   */
  pickerProps: IDateTimePickerPopoverProps
}

export const DateTimeBottomSheet: React.FC<IDateTimeBottomSheetProps> = ({
  isOpen,
  onClose,
  title,
  pickerProps,
}) => {
  const pickerRef = React.useRef<DateTimePickerPopover>(null)
  const [canApply, setCanApply] = React.useState(false)
  const [step, setStep] = React.useState<SheetStep>('picker')
  const [pendingTimezone, setPendingTimezone] = React.useState('')

  // Reset readiness each time the sheet opens so the Apply affordance starts
  // from the picker's own initial state rather than carrying stale value.
  // The step resets too — a sheet reopening on the timezone list would be
  // showing the answer to a question the user hasn't asked yet.
  React.useEffect(() => {
    if (!isOpen) {
      setCanApply(false)
      setStep('picker')
    }
  }, [isOpen])

  const handleApply = () => {
    pickerRef.current?.apply()
  }

  // Snapshot the zone on the way in rather than reading the ref during render.
  const handleOpenTimezoneStep = () => {
    setPendingTimezone(pickerRef.current?.getTimezone() ?? '')
    setStep('timezone')
  }

  const handleSelectTimezone = (timezone: string) => {
    pickerRef.current?.setTimezone(timezone)
    setStep('picker')
  }

  const isTimezoneStep = step === 'timezone'

  return (
    <Overlay
      isOpen={isOpen}
      onClose={onClose}
      canEscapeKeyClose={true}
      canOutsideClickClose={true}
      hasBackdrop={true}
      className='c-dtBottomSheet-overlay'
      backdropClassName='c-dtBottomSheet-backdrop'
    >
      <div className='c-dtBottomSheet' role='dialog' aria-label={title}>
        <div className='c-dtBottomSheet-header'>
          {isTimezoneStep ? (
            <button
              type='button'
              className='c-dtBottomSheet-cancel'
              onClick={() => setStep('picker')}
            >
              Back
            </button>
          ) : (
            <button
              type='button'
              className='c-dtBottomSheet-cancel'
              onClick={onClose}
            >
              Cancel
            </button>
          )}
          <span className='c-dtBottomSheet-title'>
            {isTimezoneStep ? 'Select timezone' : title}
          </span>
          {/* Choosing a zone is the save, so the timezone step has nothing to
              confirm — but the slot stays so the title keeps its centering. */}
          {isTimezoneStep ? (
            <span className='c-dtBottomSheet-apply' aria-hidden='true' />
          ) : (
            <button
              type='button'
              className='c-dtBottomSheet-apply'
              disabled={!canApply}
              onClick={handleApply}
            >
              Apply
            </button>
          )}
        </div>
        {/* The picker stays mounted behind the timezone step — unmounting it
            would drop the pending date and time the user has already set. */}
        <div className='c-dtBottomSheet-content' hidden={isTimezoneStep}>
          <DateTimePickerPopover
            ref={pickerRef}
            {...pickerProps}
            hideFooter={true}
            inline={true}
            onClose={onClose}
            onCanApplyChange={setCanApply}
            onRequestTimezonePicker={
              pickerProps.showTimezoneInput ? handleOpenTimezoneStep : undefined
            }
          />
        </div>
        {isTimezoneStep && (
          <TimezoneListStep selected={pendingTimezone} onSelect={handleSelectTimezone} />
        )}
      </div>
    </Overlay>
  )
}
